import React, { useState } from 'react';
import { FileText, Check, X, ZoomIn, AlertTriangle, Eye, ArrowLeft, UserCheck, HelpCircle } from 'lucide-react';
import { Page } from '../components/Layout';
const CLAIM_PDF = 'https://cdn.magicpatterns.com/uploads/7ZdRftQEiAEJFYNVwrreEE/image.png';
const BOL_PDF = 'https://cdn.magicpatterns.com/uploads/69cK22deVhYzwNxexZYGh1/image.png';
function PdfModal({
  src,
  title,
  onClose,
  isGrey = false
}: {
  src: string;
  title: string;
  onClose: () => void;
  isGrey?: boolean;
}) {
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-6" onClick={onClose} data-id="element-599">
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-600">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 flex-shrink-0" data-id="element-601">
          <div className="flex items-center gap-2" data-id="element-602">
            <FileText className="w-4 h-4 text-blue-600" data-id="element-603" />
            <span className="text-sm font-semibold text-slate-800" data-id="element-604">
              {title}
            </span>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors" data-id="element-605">
            <X className="w-4 h-4" data-id="element-606" />
          </button>
        </div>
        <div className="flex-1 overflow-auto p-4" data-id="element-607">
          {isGrey ? <div className="w-full rounded-lg bg-slate-200" style={{
          minHeight: '400px'
        }} data-id="element-608" /> : <img src={src} alt={title} className="w-full rounded-lg" data-id="element-609" />}
        </div>
      </div>
    </div>;
}
export function InvestigationWorkbenchHumanReview({
  onNavigate,
  onBackToCheck
}: {
  onNavigate?: (page: Page) => void;
  onBackToCheck?: () => void;
}) {
  const [claimPdfOpen, setClaimPdfOpen] = useState(false);
  const [bolPdfOpen, setBolPdfOpen] = useState(false);
  const handleBack = () => {
    if (onBackToCheck) onBackToCheck();
    if (onNavigate) onNavigate('checks');
  };
  return <div className="space-y-6 h-full flex flex-col" data-id="element-610">
      {claimPdfOpen && <PdfModal src={CLAIM_PDF} title="Claim Document — Dollar General Chargeback" onClose={() => setClaimPdfOpen(false)} data-id="element-611" />}
      {bolPdfOpen && <PdfModal src={BOL_PDF} title="Bill of Lading" onClose={() => setBolPdfOpen(false)} isGrey={true} data-id="element-612" />}

      <div data-id="element-613">
        <button onClick={handleBack} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 transition-colors mb-4" data-id="element-614">
          <ArrowLeft className="w-4 h-4" data-id="element-615" />
          Back to Check #DG-8824
        </button>

        <div className="flex items-center justify-between" data-id="element-616">
          <div data-id="element-617">
            <h2 className="text-2xl font-bold text-slate-900 tracking-tight" data-id="element-618">
              AI Audit & Review
            </h2>
            <p className="text-slate-500 mt-1" data-id="element-619">
              Review AI findings and approve disputes.
            </p>
          </div>
          <div className="flex space-x-3" data-id="element-620">
            <div className="px-4 py-2 bg-amber-100 text-amber-700 rounded-lg text-sm font-semibold border border-amber-200 flex items-center space-x-2" data-id="element-621">
              <UserCheck className="w-4 h-4" data-id="element-622" />
              <span data-id="element-623">Needs Human Review</span>
            </div>
          </div>
        </div>
      </div>

      {/* Case Header */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm flex items-center justify-between" data-id="element-624">
        <div className="flex items-center space-x-6" data-id="element-625">
          <div data-id="element-626">
            <span className="text-xs text-slate-500 uppercase tracking-wider font-semibold" data-id="element-627">
              Retailer
            </span>
            <div className="font-bold text-slate-900" data-id="element-628">Dollar General</div>
          </div>
          <div className="h-8 w-px bg-slate-200" data-id="element-629"></div>
          <div data-id="element-630">
            <span className="text-xs text-slate-500 uppercase tracking-wider font-semibold" data-id="element-631">
              Deduction ID
            </span>
            <div className="font-medium text-slate-900" data-id="element-632">PO-88204</div>
          </div>
          <div className="h-8 w-px bg-slate-200" data-id="element-633"></div>
          <div data-id="element-634">
            <span className="text-xs text-slate-500 uppercase tracking-wider font-semibold" data-id="element-635">
              Type
            </span>
            <div className="font-medium text-slate-900" data-id="element-636">Short Quantity</div>
          </div>
          <div className="h-8 w-px bg-slate-200" data-id="element-637"></div>
          <div data-id="element-638">
            <span className="text-xs text-slate-500 uppercase tracking-wider font-semibold" data-id="element-639">
              Amount
            </span>
            <div className="font-bold text-rose-600" data-id="element-640">-$850</div>
          </div>
          <div className="h-8 w-px bg-slate-200" data-id="element-641"></div>
          <div data-id="element-642">
            <span className="text-xs text-slate-500 uppercase tracking-wider font-semibold" data-id="element-643">
              Invoice Number
            </span>
            <div className="font-medium text-slate-900" data-id="element-644">INV-2024-019</div>
          </div>
        </div>
      </div>

      {/* 3-Way Match View */}
      <div className="flex-1 grid grid-cols-3 gap-6 min-h-0" data-id="element-645">
        {/* Column 1: The Claim (Retailer) */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm flex flex-col overflow-hidden" data-id="element-646">
          <div className="p-4 bg-slate-50 border-b border-slate-200 flex justify-between items-center" data-id="element-647">
            <div className="flex items-center space-x-2" data-id="element-648">
              <FileText className="w-4 h-4 text-slate-500" data-id="element-649" />
              <h3 className="font-bold text-slate-700" data-id="element-650">The Claim (Retailer)</h3>
            </div>
            <span className="text-xs font-medium text-slate-600 bg-slate-100 px-2 py-1 rounded" data-id="element-651">
              Source: Deduction PDF
            </span>
          </div>

          <div className="p-6 space-y-6 flex-1 overflow-y-auto custom-scrollbar" data-id="element-652">
            <div className="space-y-1" data-id="element-653">
              <label className="text-xs font-semibold text-slate-500 uppercase" data-id="element-654">
                Claim Code
              </label>
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 text-slate-900 font-medium" data-id="element-655">
                Short Shipment (Code 11)
              </div>
            </div>

            <div className="space-y-1" data-id="element-656">
              <label className="text-xs font-semibold text-slate-500 uppercase" data-id="element-657">
                Item / SKU
              </label>
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 text-slate-900" data-id="element-658">
                <div className="font-medium" data-id="element-659">Colgate Total Toothpaste</div>
                <div className="text-xs text-slate-500" data-id="element-660">SKU: 987-654-321</div>
              </div>
            </div>

            <div className="space-y-1" data-id="element-661">
              <label className="text-xs font-semibold text-slate-500 uppercase" data-id="element-662">
                Units Claimed Received
              </label>
              <div className="p-3 bg-amber-50 rounded-lg border border-amber-200 text-amber-700 font-bold text-lg flex justify-between items-center" data-id="element-663">
                <span data-id="element-664">100 units</span>
                <AlertTriangle className="w-5 h-5 text-amber-500" data-id="element-665" />
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-slate-100" data-id="element-666">
              <div className="flex items-center justify-between mb-2" data-id="element-667">
                <label className="text-xs font-semibold text-slate-500 uppercase" data-id="element-668">
                  Claim Document
                </label>
                <button onClick={() => setClaimPdfOpen(true)} className="text-xs text-blue-600 hover:underline flex items-center" data-id="element-669">
                  <Eye className="w-3 h-3 mr-1" data-id="element-670" /> View Full PDF
                </button>
              </div>
              <div onClick={() => setClaimPdfOpen(true)} className="rounded-lg border border-slate-200 overflow-hidden relative group cursor-pointer" data-id="element-671">
                <img src={CLAIM_PDF} alt="Claim PDF Snippet" className="w-full object-cover object-top" style={{
                maxHeight: '160px'
              }} data-id="element-672" />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center" data-id="element-673">
                  <ZoomIn className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow" data-id="element-674" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Column 2: Internal Records */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm flex flex-col overflow-hidden" data-id="element-675">
          <div className="p-4 bg-slate-50 border-b border-slate-200 flex justify-between items-center" data-id="element-676">
            <div className="flex items-center space-x-2" data-id="element-677">
              <FileText className="w-4 h-4 text-slate-500" data-id="element-678" />
              <h3 className="font-bold text-slate-700" data-id="element-679">Internal Records</h3>
            </div>
            <span className="text-xs font-medium text-slate-600 bg-slate-100 px-2 py-1 rounded" data-id="element-680">
              Source: ERP + BOL
            </span>
          </div>

          <div className="p-6 space-y-6 flex-1 overflow-y-auto custom-scrollbar" data-id="element-681">
            <div className="space-y-1" data-id="element-682">
              <label className="text-xs font-semibold text-slate-500 uppercase" data-id="element-683">
                Invoice Number
              </label>
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 text-slate-900 font-medium" data-id="element-684">
                INV-2024-019
              </div>
            </div>

            <div className="space-y-1" data-id="element-685">
              <label className="text-xs font-semibold text-slate-500 uppercase" data-id="element-686">
                Item / SKU
              </label>
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 text-slate-900" data-id="element-687">
                <div className="font-medium" data-id="element-688">Colgate Total Toothpaste</div>
                <div className="text-xs text-slate-500" data-id="element-689">SKU: 987-654-321</div>
              </div>
            </div>

            <div className="space-y-1" data-id="element-690">
              <label className="text-xs font-semibold text-slate-500 uppercase" data-id="element-691">
                Units Shipped (BOL)
              </label>
              <div className="p-3 bg-amber-50 rounded-lg border border-amber-200 text-amber-700 font-bold text-lg flex justify-between items-center" data-id="element-692">
                <span data-id="element-693">Unsure</span>
                <AlertTriangle className="w-5 h-5 text-amber-500" data-id="element-694" />
              </div>
            </div>

            <div className="space-y-1" data-id="element-695">
              <div className="flex justify-between items-center mb-2" data-id="element-696">
                <label className="text-xs font-semibold text-slate-500 uppercase" data-id="element-697">
                  BOL Document
                </label>
                <button onClick={() => setBolPdfOpen(true)} className="text-xs text-blue-600 hover:underline flex items-center" data-id="element-698">
                  <Eye className="w-3 h-3 mr-1" data-id="element-699" /> View Full PDF
                </button>
              </div>
              <div onClick={() => setBolPdfOpen(true)} className="rounded-lg border border-slate-200 overflow-hidden relative group cursor-pointer" data-id="element-700">
                <div className="w-full bg-slate-200" style={{
                height: '160px'
              }} data-id="element-701" />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center" data-id="element-702">
                  <ZoomIn className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow" data-id="element-703" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Column 3: AI Verdict — Low Confidence */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm flex flex-col overflow-hidden ring-1 ring-amber-400/30" data-id="element-704">
          <div className="p-4 bg-amber-50 border-b border-amber-100 flex justify-between items-center" data-id="element-705">
            <div className="flex items-center space-x-2" data-id="element-706">
              <HelpCircle className="w-4 h-4 text-amber-600" data-id="element-707" />
              <h3 className="font-bold text-amber-900" data-id="element-708">AI Verdict</h3>
            </div>
            <div className="flex items-center gap-2" data-id="element-709">
              <span className="text-xs font-bold text-amber-700 bg-amber-100 px-2 py-1 rounded" data-id="element-710">
                54% Match
              </span>
            </div>
          </div>

          <div className="p-6 space-y-6 flex-1 overflow-y-auto custom-scrollbar" data-id="element-711">
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg" data-id="element-712">
              <div className="flex items-center space-x-2 mb-2" data-id="element-713">
                <AlertTriangle className="w-5 h-5 text-amber-500" data-id="element-714" />
                <span className="font-bold text-amber-700" data-id="element-715">
                  Low Confidence — Human Review Required
                </span>
              </div>
              <p className="text-sm text-amber-800" data-id="element-716">
                Cannot verify whether 100 units were actually short-shipped or a
                receiving error occurred.
              </p>
            </div>

            <div className="space-y-3" data-id="element-717">
              <h4 className="text-sm font-semibold text-slate-900" data-id="element-718">
                Recommended Action
              </h4>
              <div className="flex flex-col space-y-2" data-id="element-719">
                <button className="w-full p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center justify-between group hover:bg-emerald-100 transition-colors text-left" data-id="element-720">
                  <div data-id="element-721">
                    <div className="font-bold text-emerald-700" data-id="element-722">
                      Approve Dispute
                    </div>
                    <div className="text-xs text-emerald-600" data-id="element-723">
                      Generate email packet & queue. Review email draft below
                      and make edits if required
                    </div>
                  </div>
                  <Check className="w-5 h-5 text-emerald-500 group-hover:scale-110 transition-transform" data-id="element-724" />
                </button>
                <button className="w-full p-3 bg-white border border-slate-200 rounded-lg flex items-center justify-between group hover:bg-slate-50 transition-colors text-left" data-id="element-725">
                  <div data-id="element-726">
                    <div className="font-medium text-slate-700" data-id="element-727">Write Off</div>
                    <div className="text-xs text-slate-500" data-id="element-728">
                      Amount too small or evidence weak
                    </div>
                  </div>
                  <X className="w-5 h-5 text-slate-400" data-id="element-729" />
                </button>
              </div>
            </div>

            <div className="pt-6 border-t border-slate-100" data-id="element-730">
              <h4 className="text-sm font-semibold text-slate-900 mb-3" data-id="element-731">
                Draft Email Preview
              </h4>
              <div className="bg-slate-50 rounded-lg border border-slate-200 overflow-hidden" data-id="element-732">
                <div className="px-3 py-2 border-b border-slate-200 bg-white" data-id="element-733">
                  <p className="text-xs text-slate-700 leading-relaxed" data-id="element-734">
                    <span className="font-bold text-slate-900" data-id="element-735">Subject:</span>{' '}
                    Dispute – PO# 88204 – Shortage Claim
                  </p>
                </div>
                <textarea className="w-full bg-slate-50 px-3 py-3 text-xs text-slate-700 leading-relaxed resize-none focus:outline-none focus:bg-white transition-colors" rows={8} defaultValue={`Dear AP Team,

Regarding Deduction #PO-88204 for Short Quantity.

We invoiced 100 units of Colgate Total Toothpaste (SKU: 987-654-321). Our records indicate the full quantity was dispatched; however, we are currently unable to confirm the exact units shipped via BOL and are requesting manual verification.

Please repay the shortage of $850 or provide supporting documentation for the deduction.

Thank you,
ZenClaims AR Team`} data-id="element-736" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}